import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { createBooking, checkAvailability } from '../../utils/supabaseExtended';
import { createPaymentIntent, calculateBookingCosts } from '../../utils/stripeConfig';
import { useAuth } from '../../utils/AuthContext';
import { channelSyncManager } from '../../utils/channelSync';
import Button from './Button';
import { CheckIcon, ClockIcon } from '../../utils/IconWrapper';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_...');

const BookingContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid #e1e5e9;
`;

const FormSection = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;

  &:focus {
    border-color: #0d9488;
    outline: none;
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const DateGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const PricingBreakdown = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin: 1.5rem 0;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  
  &.total {
    font-weight: bold;
    font-size: 1.2rem;
    border-top: 1px solid #dee2e6;
    padding-top: 0.5rem;
    margin-top: 1rem;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SuccessMessage = styled.div`
  color: #28a745;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const StyledCardElement = styled(CardElement)`
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  background: white;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #0d9488;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface BookingFormProps {
  accommodationId: number;
  accommodationTitle: string;
  basePrice: number;
  maxGuests: number;
}

const BookingFormComponent: React.FC<BookingFormProps> = ({
  accommodationId,
  accommodationTitle,
  basePrice,
  maxGuests
}) => {
  const { user } = useAuth();
  const stripe = useStripe();
  const elements = useElements();

  // Form state
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(1);
  const [guestName, setGuestName] = useState(user?.email?.split('@')[0] || '');
  const [guestEmail, setGuestEmail] = useState(user?.email || '');
  const [guestPhone, setGuestPhone] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');

  // Pricing
  const [pricing, setPricing] = useState<any>(null);
  const [nights, setNights] = useState(0);

  // Status
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [availability, setAvailability] = useState<{ available: boolean; message?: string }>({ available: true });

  // Calculate pricing when dates change
  useEffect(() => {
    if (checkIn && checkOut) {
      const startDate = new Date(checkIn);
      const endDate = new Date(checkOut);
      const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
      const calculatedNights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (calculatedNights > 0) {
        setNights(calculatedNights);
        const pricingCalc = calculateBookingCosts(basePrice, calculatedNights);
        setPricing(pricingCalc);
      }
    }
  }, [checkIn, checkOut, basePrice]);

  // Check availability when dates change
  useEffect(() => {
    const checkDatesAvailability = async () => {
      if (checkIn && checkOut) {
        try {
          const result = await checkAvailability(accommodationId, checkIn, checkOut);
          setAvailability(result);
        } catch (error) {
          setAvailability({ available: false, message: 'Errore nel controllo disponibilità' });
        }
      }
    };

    checkDatesAvailability();
  }, [checkIn, checkOut, accommodationId]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements || !user) {
      setError('Sistema non pronto o utente non autenticato');
      return;
    }

    if (!availability.available) {
      setError('Date selezionate non disponibili');
      return;
    }

    if (!pricing) {
      setError('Seleziona le date per continuare');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // 1. Create booking
      const bookingResult = await createBooking({
        accommodationId,
        checkIn,
        checkOut,
        guestsCount: guests,
        guestName,
        guestEmail,
        guestPhone,
        specialRequests
      });

      if (!bookingResult.success || !bookingResult.booking) {
        throw new Error(bookingResult.error || 'Errore nella creazione della prenotazione');
      }

      // 2. Create payment intent
      const paymentIntentResult = await createPaymentIntent({
        amount: Math.round(pricing.total * 100), // Convert to cents
        currency: 'eur',
        bookingId: bookingResult.booking.id,
        userId: user.id,
        metadata: {
          accommodationId: accommodationId.toString(),
          checkIn,
          checkOut,
          guestName,
          guestEmail
        }
      });

      if (!paymentIntentResult.success || !paymentIntentResult.clientSecret) {
        throw new Error(paymentIntentResult.error || 'Errore nella creazione del pagamento');
      }

      // 3. Confirm payment
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Elemento carta non trovato');
      }

      const { error: paymentError, paymentIntent } = await stripe.confirmCardPayment(
        paymentIntentResult.clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: guestName,
              email: guestEmail,
              phone: guestPhone
            }
          }
        }
      );

      if (paymentError) {
        throw new Error(paymentError.message || 'Errore nel pagamento');
      }

      if (paymentIntent?.status === 'succeeded') {
        setSuccess('Prenotazione confermata! Riceverai un\'email di conferma.');
        
        // 4. Sincronizza con canali esterni (Airbnb, AvaiBook)
        try {
          await channelSyncManager.onDirectBookingCreated(
            accommodationId,
            checkIn,
            checkOut
          );
          console.log('✅ Disponibilità sincronizzata su tutti i canali');
        } catch (syncError) {
          console.error('⚠️ Errore sincronizzazione canali:', syncError);
          // Non bloccare il successo della prenotazione per errori di sync
        }
        
        // Reset form
        setCheckIn('');
        setCheckOut('');
        setGuests(1);
        setSpecialRequests('');
        setPricing(null);
      }

    } catch (err: any) {
      setError(err.message || 'Errore sconosciuto');
    } finally {
      setIsLoading(false);
    }
  };

  // Get today's date for min attribute
  const today = new Date().toISOString().split('T')[0];

  return (
    <BookingContainer>
      <h3 style={{ marginBottom: '1.5rem', color: '#333' }}>
        Prenota "{accommodationTitle}"
      </h3>

      <form onSubmit={handleSubmit}>
        <DateGrid>
          <FormSection>
            <Label>Check-in</Label>
            <Input
              type="date"
              value={checkIn}
              onChange={(e) => setCheckIn(e.target.value)}
              min={today}
              required
            />
          </FormSection>

          <FormSection>
            <Label>Check-out</Label>
            <Input
              type="date"
              value={checkOut}
              onChange={(e) => setCheckOut(e.target.value)}
              min={checkIn || today}
              required
            />
          </FormSection>
        </DateGrid>

        <FormSection>
          <Label>Numero di Ospiti</Label>
          <Input
            type="number"
            value={guests}
            onChange={(e) => setGuests(parseInt(e.target.value))}
            min="1"
            max={maxGuests}
            required
          />
        </FormSection>

        <FormSection>
          <Label>Nome Completo</Label>
          <Input
            type="text"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            required
          />
        </FormSection>

        <FormSection>
          <Label>Email</Label>
          <Input
            type="email"
            value={guestEmail}
            onChange={(e) => setGuestEmail(e.target.value)}
            required
          />
        </FormSection>

        <FormSection>
          <Label>Telefono (opzionale)</Label>
          <Input
            type="tel"
            value={guestPhone}
            onChange={(e) => setGuestPhone(e.target.value)}
          />
        </FormSection>

        <FormSection>
          <Label>Richieste Speciali (opzionale)</Label>
          <Input
            as="textarea"
            value={specialRequests}
            onChange={(e) => setSpecialRequests(e.target.value)}
            rows={3}
            style={{ resize: 'vertical', minHeight: '80px' }}
          />
        </FormSection>

        {/* Availability Status */}
        {checkIn && checkOut && (
          <div style={{ margin: '1rem 0' }}>
            {availability.available ? (
              <SuccessMessage>
                <CheckIcon style={{ marginRight: '0.5rem' }} />
                Date disponibili
              </SuccessMessage>
            ) : (
              <ErrorMessage>
                <ClockIcon style={{ marginRight: '0.5rem' }} />
                {availability.message || 'Date non disponibili'}
              </ErrorMessage>
            )}
          </div>
        )}

        {/* Pricing Breakdown */}
        {pricing && nights > 0 && (
          <PricingBreakdown>
            <h4 style={{ marginBottom: '1rem' }}>Riepilogo Prezzi</h4>
            <PriceRow>
              <span>€{basePrice} × {nights} notti</span>
              <span>€{pricing.subtotal.toFixed(2)}</span>
            </PriceRow>
            <PriceRow>
              <span>Pulizie</span>
              <span>€{pricing.cleaningFee.toFixed(2)}</span>
            </PriceRow>
            <PriceRow>
              <span>Commissioni servizio</span>
              <span>€{pricing.serviceFee.toFixed(2)}</span>
            </PriceRow>
            <PriceRow>
              <span>Tasse</span>
              <span>€{pricing.taxes.toFixed(2)}</span>
            </PriceRow>
            <PriceRow className="total">
              <span>Totale</span>
              <span>€{pricing.total.toFixed(2)}</span>
            </PriceRow>
          </PricingBreakdown>
        )}

        {/* Payment Method */}
        <FormSection>
          <Label>Metodo di Pagamento</Label>
          <StyledCardElement 
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
              },
            }}
          />
        </FormSection>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <Button
          type="submit"
          disabled={isLoading || !availability.available || !pricing || !user}
          style={{
            width: '100%',
            marginTop: '1rem',
            backgroundColor: '#0d9488',
            padding: '1rem'
          }}
        >
          {isLoading ? (
            <>
              <LoadingSpinner style={{ marginRight: '0.5rem' }} />
              Elaborazione...
            </>
          ) : (
            `Prenota ora - €${pricing?.total.toFixed(2) || '0.00'}`
          )}
        </Button>
      </form>
    </BookingContainer>
  );
};

const BookingForm: React.FC<BookingFormProps> = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <BookingFormComponent {...props} />
    </Elements>
  );
};

export default BookingForm; 